import React, { useState, useEffect } from "react";
import { Row, Col, Form, Table, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../../config/firebase";
import PageHeader from "components/common/PageHeader";
import FalconComponentCard from "components/common/FalconComponentCard";
import committees from "../../config/committees";
import committeesRoles from "../../config/committeesRoles";
import ActionModal from "./ActionModal";

const EditMember = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [memberData, setMemberData] = useState(null);
  const [committeeData, setCommitteeData] = useState(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedCommittee, setSelectedCommittee] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const id = window !== undefined && window.location.pathname.split("/").pop();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        const uid = user.uid;
      } else {
        navigate("/login");
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (id) {
        try {
          const apiUrl = `${process.env.REACT_APP_API_URL}`;
          const response1 = await axios.get(
            `${apiUrl}/members/committees/id/${id}`
          );
          const response2 = await axios.get(`${apiUrl}/members/id/${id}`);

          setCommitteeData(response1.data);
          setMemberData(response2.data[0]);

          setLoading(false);
        } catch (err) {
          console.log("Error", err);
        }
      } else {
        navigate("/members/list");
      }
    };

    fetchData();
  }, []);

  const getCommitteeName = (committeeId) => {
    const committee = committees.find(
      (committee) => committee.value === committeeId
    );
    return committee ? committee.name : "";
  };

  const handleCreateModalClose = () => {
    setSelectedCommittee(null);
    setShowCreateModal(false);
  };

  const handleRoleModalClose = () => {
    setSelectedCommittee(null);
    setShowRoleModal(false);
  };

  const handleDeleteModalClose = () => {
    setSelectedCommittee(null);
    setShowDeleteModal(false);
  };

  const handleDeleteClick = (committee) => {
    setSelectedCommittee(committee);
    setShowDeleteModal(true);
  };

  const handleDeleteRole = async () => {
    try {
      const apiUrl = `${process.env.REACT_APP_API_URL}/members/archive/committee/${selectedCommittee.id}`;
      const response = await axios.delete(apiUrl);

      if (response.status === 200) {
        toast.success("Role deleted successfully", {
          theme: "colored",
        });
        const newCommitteeData = committeeData.filter(
          (committee) => committee.id !== selectedCommittee.id
        );
        setCommitteeData(newCommitteeData);
        handleDeleteModalClose();
      } else {
        toast.error("Failed to delete role", {
          theme: "colored",
        });
      }
    } catch (err) {
      console.error("Error", err.message);
      toast.error(`Error: ${err.message}`, {
        theme: "colored",
      });
    }
  };

  const handleRoleClick = (committee) => {
    setSelectedCommittee(committee);
    setShowRoleModal(true);
  };

  const handleRoleChange = (e) => {
    console.log("role", e.target.value);
    const role = e.target.value;
    setSelectedCommittee({
      ...selectedCommittee,
      role,
    });
  };

  const handleCommitteeChange = (e) => {
    console.log("selectedCommittee", e.target.value);
    const committee_id = e.target.value;
    setSelectedCommittee({
      ...selectedCommittee,
      committee_id,
    });
  };

  const handleModalCreateCommittee = () => {
    setShowCreateModal(true);
  };

  const handleRoleUpdate = async () => {
    try {
      const apiUrl = `${process.env.REACT_APP_API_URL}/members/committee/${selectedCommittee.id}/${selectedCommittee.role}`;
      const response = await axios.put(apiUrl);

      if (response.status === 200) {
        toast.success("Role updated successfully", {
          theme: "colored",
        });
        const newCommitteeData = committeeData.map((committee) =>
          committee.id === selectedCommittee.id ? selectedCommittee : committee
        );
        setCommitteeData(newCommitteeData);
        handleRoleModalClose();
      } else {
        toast.error("Failed to update role", {
          theme: "colored",
        });
      }
    } catch (err) {
      console.error("Error", err.message);
      toast.error(`Error: ${err.message}`, {
        theme: "colored",
      });
    }
  };

  const handleSaveNew = async () => {
    console.log("selectedCommittee", selectedCommittee, id);
    if (
      !selectedCommittee?.committee_id ||
      !selectedCommittee?.role ||
      id == null
    ) {
      toast.error("Please select a committee and role", {
        theme: "colored",
      });
      return;
    }
    try {
      const apiUrl = `${process.env.REACT_APP_API_URL}/members/create/committee/${selectedCommittee.committee_id}/role/${selectedCommittee.role}/member/${id}`;
      const response = await axios.post(apiUrl);

      if (response.status === 200) {
        toast.success("Role created successfully", {
          theme: "colored",
        });
        const newCommitteeData = [...committeeData, selectedCommittee];
        setCommitteeData(newCommitteeData);
        handleCreateModalClose();
      } else {
        toast.error("Failed to create role", {
          theme: "colored",
        });
      }
    } catch (err) {
      console.error("Error", err.message);
      toast.error(`Error: ${err.message}`, {
        theme: "colored",
      });
    }
  };

  return (
    <>
      <PageHeader
        title="Manage a Member's Committees"
        description="The updated Member details will be shown on the Member's page"
        className="mb-3"
      ></PageHeader>

      {!loading ? (
        <FalconComponentCard>
          <FalconComponentCard.Header
            title={`Member: ${memberData?.name} ${memberData?.surname}`}
            light={false}
            noPreview={true}
          ></FalconComponentCard.Header>

          {!loading && (
            <FalconComponentCard.Body>
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Role</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {committeeData.length > 0 &&
                    committeeData.map((committee) => (
                      <tr>
                        <td>
                          {getCommitteeName(committee?.committee_id.toString())}
                        </td>
                        <td>{committee.role}</td>
                        <td>
                          <Button
                            variant="primary"
                            onClick={() => handleRoleClick(committee)}
                          >
                            Edit
                          </Button>{" "}
                          <Button
                            variant="danger"
                            onClick={() => handleDeleteClick(committee)}
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </Table>

              <ActionModal
                show={showCreateModal}
                handleClose={handleCreateModalClose}
                title="Create role"
                handleSave={handleSaveNew}
              >
                <Form.Group>
                  <Form.Label>Committee</Form.Label>
                  <Form.Control as="select" onChange={handleCommitteeChange}>
                    {committees.map((committee) => (
                      <option value={committee.value}>{committee.name}</option>
                    ))}
                  </Form.Control>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Role</Form.Label>
                  <Form.Control
                    as="select"
                    defaultValue={selectedCommittee?.role}
                    onChange={handleRoleChange}
                  >
                    <option value="">Select role</option>
                    {committeesRoles.map((role) => (
                      <option value={role.value}>{role.name}</option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </ActionModal>

              <ActionModal
                show={showRoleModal}
                handleClose={handleRoleModalClose}
                title="Edit member's committee role"
                handleSave={handleRoleUpdate}
              >
                <Form.Group>
                  <Form.Label>Role</Form.Label>
                  <Form.Control
                    as="select"
                    defaultValue={selectedCommittee?.role}
                    onChange={handleRoleChange}
                  >
                    {committeesRoles.map((role) => (
                      <option value={role.value}>{role.name}</option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </ActionModal>

              <ActionModal
                show={showDeleteModal}
                handleClose={handleDeleteModalClose}
                title="Delete role"
                handleSave={handleDeleteRole}
              >
                <p>Are you sure you want to delete this role?</p>
              </ActionModal>

              <Row>
                <Col lg="6" className="mt-4 mb-3 ml-3">
                  <Form.Group>
                    <Button
                      variant="primary"
                      onClick={handleModalCreateCommittee}
                    >
                      Add new committee membership for {memberData.name}
                    </Button>
                  </Form.Group>
                </Col>
              </Row>
            </FalconComponentCard.Body>
          )}
        </FalconComponentCard>
      ) : (
        <p>Loading...</p>
      )}
    </>
  );
};

export default EditMember;
